import { Alert, Button, Drawer, Form, Grid, Input, Select, Space, Tag } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { allRoles, roleTagColor, type Role } from "../app/roles.ts";
import { applyProblemToForm, requestFailure, type RequestFailure } from "../app/form-errors.ts";

type CreatedUser = components["schemas"]["CreatedUserResponse"];

interface UserForm {
  userName: string;
  displayName: string;
  email?: string;
  phone?: string;
  roles: Role[];
  password?: string;
}

const formFields = ["userName", "displayName", "email", "phone", "roles", "password"] as const;

interface Props {
  open: boolean;
  onClose(): void;
  onCreated(result: CreatedUser): void;
}

export function CreateUserDrawer({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<UserForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: async (values: UserForm) => {
      const { data, error, response } = await api.POST("/users", {
        body: {
          userName: values.userName.trim(),
          displayName: values.displayName.trim(),
          email: values.email?.trim() || null,
          phone: values.phone?.trim() || null,
          roles: values.roles,
          password: values.password || null,
        },
      });
      if (!data) throw requestFailure(error, response.status);
      return data;
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      form.resetFields();
      setServerErrors([]);
      onCreated(result);
    },
    onError: (failure: RequestFailure) => setServerErrors(applyProblemToForm(failure, form, formFields, t("app.error"))),
  });

  return (
    <Drawer
      title={t("users.create")}
      open={open}
      onClose={onClose}
      size={screens.md ? 520 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("user.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
            {t("user.save")}
          </Button>
        </Space>
      }
    >
      {serverErrors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
      )}
      <Form<UserForm> form={form} layout="vertical" requiredMark={false} initialValues={{ roles: [] }} onFinish={(values) => mutation.mutate(values)} autoComplete="off">
        <Form.Item name="displayName" label={t("user.displayName")} rules={[{ required: true, whitespace: true, message: t("user.validation.required") }]}>
          <Input maxLength={200} autoFocus />
        </Form.Item>
        <Form.Item
          name="userName"
          label={t("user.userName")}
          extra={t("user.userNameHint")}
          rules={[
            { required: true, message: t("user.validation.required") },
            { pattern: /^[A-Za-z0-9][A-Za-z0-9._-]{2,63}$/, message: t("user.validation.userName") },
          ]}
        >
          <Input maxLength={64} autoComplete="off" />
        </Form.Item>
        <div style={{ display: "grid", gridTemplateColumns: screens.sm ? "1fr 1fr" : "1fr", gap: 12 }}>
          <Form.Item name="email" label={t("user.email")} rules={[{ type: "email", message: t("user.validation.email") }]}>
            <Input inputMode="email" maxLength={254} />
          </Form.Item>
          <Form.Item name="phone" label={t("user.phone")} rules={[{ pattern: /^[\d\s()+-]{9,20}$/, message: t("user.validation.phone") }]}>
            <Input inputMode="tel" placeholder={t("user.phoneHint")} />
          </Form.Item>
        </div>
        <Form.Item name="roles" label={t("user.roles")} extra={t("user.rolesHint")} rules={[{ required: true, type: "array", min: 1, message: t("user.validation.roles") }]}>
          <Select<Role[]>
            mode="multiple"
            showSearch={{ optionFilterProp: "label" }}
            placeholder={t("user.rolesPlaceholder")}
            options={allRoles.map((r) => ({ value: r, label: t(`roles.${r}`) }))}
            tagRender={({ label, value, closable, onClose: close }) => (
              <Tag color={roleTagColor[value as Role]} closable={closable} onClose={close} style={{ marginInlineEnd: 4 }}>
                {label}
              </Tag>
            )}
          />
        </Form.Item>
        <Form.Item name="password" label={t("user.password")} extra={t("user.passwordHint")} rules={[{ min: 8, message: t("user.validation.password") }]}>
          <Input.Password autoComplete="new-password" maxLength={256} />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
